/* eslint-disable @typescript-eslint/no-explicit-any */
import { useAppDispatch } from '../../reducers/store'
import { fetchCustomDnsConfiguration, postCustomDnsConfiguration, selectSettings } from '../../reducers/settings'
import { useSelector } from 'react-redux'
import { Button, Card, Flex, Switch, Tooltip } from '@radix-ui/themes'
import { useEffect, useState } from 'react'
import { CustomDnsStatus } from '../../types/SettingsType'
import { DEFAULT_TOAST_CONFIG } from '../toast/consts'
import toast from 'react-hot-toast'
import RadioInput, {
  RadioInputConfiguration as RadioInputConfiguration,
  RadioInputOption,
} from '../input/CustomDnsRadioInput'
import { IconInfoCircle } from '@tabler/icons-react'
import { selectUser } from '../../reducers/users'
import SecureDnsSkeleton from '../skeleton/SecureDnsSkeleton'

function SecureDns() {
  const defaultOptions = [
    { ipAddress: '1.1.1.2', label: 'Cloudflare', hostname: 'security.cloudflare-dns.com' },
    { ipAddress: '8.8.8.8', label: 'Google', hostname: 'dns.google' },
    { ipAddress: '9.9.9.9', label: 'Quad9', hostname: 'dns.quad9.net' },
  ]
  const { customDnsConfiguration } = useSelector(selectSettings)
  const dispatch = useAppDispatch()
  const di = useAppDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { darkModeEnabled } = useSelector(selectUser)
  const [androidVpnEnabled, setAndroidVpnEnabled] = useState(customDnsConfiguration?.androidVpnEnabled || false)
  const [isCustom, setIsCustom] = useState(false)
  const [options, setOptions] = useState<RadioInputOption[]>([])
  const [dnsConfig, setDnsConfig] = useState<RadioInputConfiguration>({
    textInputValue: defaultOptions[0].ipAddress,
    selectedRadio: 'iverify_dns',
  })
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const iosTip = customDnsConfiguration?.customDnsIosRequiresVPN
      ? 'iOS: The DNS provider for the VPN will be affected.'
      : 'iOS: The DNS provider for the DNS extension will be affected.'
    const androidTip = androidVpnEnabled
      ? 'Android: The DNS provider for the VPN will be affected.'
      : 'Android: The Private DNS provider hostname options will be affected.'
    setOptions([
      {
        label: 'Enable Secure DNS',
        hostname:
          'Uses Cloudflare as the default DNS provider. Users can switch to Google or Quad9 in the iVerify app.',
        tips: [iosTip, androidTip],
        type: 'label',
        ipAddress: 'iverify_dns',
      },
      {
        label: 'Enable Secure DNS, and force provider',
        hostname: 'Forces a DNS provider from the list. Users cannot switch to another provider.',
        tips: [iosTip, androidTip],
        type: 'text_input',
        ipAddress: 'custom_dns',
        placeholder: 'Enter a Custom DNS',
        required: true,
        inputName: 'custom_dns_url',
      },
      {
        label: 'Disable Secure DNS ',
        hostname: 'iVerify will not set the Secure DNS provider',
        tips: [
          customDnsConfiguration?.customDnsIosRequiresVPN
            ? 'iOS: The VPN will be installed without a DNS Provider.'
            : 'iOS: The DNS extension will be hidden.',
          androidVpnEnabled
            ? 'Android: The VPN will be installed without a DNS Provider.'
            : 'Android: The App will hide all DNS options and Smishing Protection will be unavailable.',
        ],
        type: 'label',
        ipAddress: 'opt_out',
      },
    ] as RadioInputOption[])
  }, [androidVpnEnabled, customDnsConfiguration])

  useEffect(() => {
    const fetchData = async () => {
      await di(fetchCustomDnsConfiguration())
        .then((res: any) => {
          if (res.error) {
            console.error('Failed to fetch custom DNS configuration:', res.error)
            toast.error(res.error, DEFAULT_TOAST_CONFIG)
          }
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
    fetchData()
  }, [])

  useEffect(() => {
    if (!customDnsConfiguration) return
    const config = {
      textInputValue: defaultOptions[0].hostname,
      selectedRadio: 'iverify_dns',
    }
    if (customDnsConfiguration) {
      config.selectedRadio =
        customDnsConfiguration.customDnsStatus === CustomDnsStatus.NETWORK_DEFAULT
          ? 'opt_out'
          : customDnsConfiguration.customDnsStatus === CustomDnsStatus.CUSTOM
            ? 'custom_dns'
            : 'iverify_dns'
      if (customDnsConfiguration.customDnsValue) {
        config.textInputValue = customDnsConfiguration.customDnsValue
      } else {
        config.textInputValue = defaultOptions[0].hostname
      }
    } else {
      config.selectedRadio = 'iverify_dns'
    }

    setDnsConfig(config)
    setAndroidVpnEnabled(customDnsConfiguration?.androidVpnEnabled || false)
    const hasMatch = defaultOptions.some(
      (o) =>
        o.hostname === customDnsConfiguration.customDnsValue?.replace(/^https:\/\//, '').replace(/\/dns-query$/, '')
    )
    setIsCustom(!hasMatch && customDnsConfiguration.customDnsValue != null)
  }, [customDnsConfiguration])

  const saveConfig = async (formData: FormData) => {
    const payload = {
      dnsOptOut: formData.get('sharedRadio') === 'opt_out' ? true : false,
      customDnsValue:
        formData.get('sharedRadio') !== 'custom_dns' ? undefined : formData.get('custom_dns_url')?.toString() || '',
      androidVpnEnabled: formData.get('androidVpnEnabled') === 'on' ? true : false,
    }

    await dispatch(postCustomDnsConfiguration(payload))
      .then((res: any) => {
        if (res.success == false || res.error) {
          toast.error('Failed to save DNS configuration', DEFAULT_TOAST_CONFIG)
        } else {
          toast.success('DNS configuration saved successfully', DEFAULT_TOAST_CONFIG)
        }
        di(fetchCustomDnsConfiguration())
      })
      .finally(() => {
        setIsSubmitting(false)
      })

    setIsSubmitting(false)
  }

  return (
    <div className="w-full h-[calc(100vh_-_80px)] overflow-auto">
      <Flex direction="column" gap="3" maxWidth="100%">
        {isLoading ? (
          <SecureDnsSkeleton />
        ) : (
          <form
            onSubmit={async (e) => {
              e.preventDefault()
              e.stopPropagation()
              if (isSubmitting || isCustom) return
              setIsSubmitting(true)
              const formData = new FormData(e.target as HTMLFormElement)
              saveConfig(formData)
            }}
          >
            <div className="flex pb-1 ">
              <p className="font-semibold text-xl flex-grow flex gap-1 items-center">
                Secure DNS for Smishing Protection
                <Tooltip
                  content="iVerify’s Smishing Protection uses Secure DNS to route traffic through DNS Providers, such as Google, Cloudflare or Quad9. This process does not access any additional network data, and neither iVerify or DNS Providers log DNS requests by individual users or organizations."
                  side="top"
                  align="end"
                  className="max-w-[300px]"
                >
                  <IconInfoCircle className="text-gray-500 dark:text-gray-400" size={20} />
                </Tooltip>
              </p>

              {isCustom ? (
                <div className="flex gap-4 items-center ">
                  <Button
                    onClick={() =>
                      window.open(
                        `${window.location.origin}/docs/iverify-portal-guide/Settings/Fleet-settings/secure-dns`,
                        '_blank'
                      )
                    }
                    className="text-mainText bg-neutral-2 border border-solid border-neutral-200 dark:border-neutral-600 dark:bg-neutral-800 cursor-pointer"
                  >
                    Visit Docs
                  </Button>
                  <Button
                    size="2"
                    onClick={(event) => {
                      event.preventDefault()
                      window.location.href = 'mailto:support@iverify.io'
                    }}
                    className="text-mainText bg-neutral-2 border border-solid border-neutral-200 dark:border-neutral-600 dark:bg-neutral-800 cursor-pointer"
                  >
                    Contact Support
                  </Button>
                </div>
              ) : (
                <div className="flex gap-4 items-center ">
                  <a
                    href={`${window.location.origin}/docs/iverify-portal-guide/Settings/Fleet-settings/secure-dns`}
                    target="_blank"
                    className="text-mainPurple dark:text-secondaryPurple font-medium text-sm "
                  >
                    Visit Docs
                  </a>
                  <Button
                    type="submit"
                    variant="solid"
                    className={`w-fit bg-mainPurple text-white  ${isSubmitting ? 'disabled opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-tertiaryPurple active:bg-tertiaryPurple'}`}
                    disabled={isSubmitting}
                  >
                    Save Changes
                  </Button>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-2 text-sm text-neutral-11 dark:text-neutral-8 max-w-[800px]">
                <p>These settings apply only to devices that use the app to enable DNS protection.</p>
              </div>
              <Flex direction="column" gap="3" maxWidth="100%" className="z-40 text-sm ">
                <Card variant="surface">
                  <div className="flex flex-row gap-4 px-4">
                    <div className="flex-1 flex flex-col gap-2">
                      <div className="font-semibold">Android VPN</div>
                      <div>
                        Enable to enforce Secure DNS through the Android VPN, which is required for domain blocking.
                        Disable to fall back to the device’s Private DNS settings
                      </div>
                    </div>
                    <div className="flex items-center">
                      {isCustom ? (
                        <span className="font-medium">{androidVpnEnabled ? 'On' : 'Off'}</span>
                      ) : (
                        <Switch
                          size="2"
                          className={`${darkModeEnabled ? 'dark' : 'light'}`}
                          checked={androidVpnEnabled || false}
                          onCheckedChange={setAndroidVpnEnabled}
                          name="androidVpnEnabled"
                          value="on"
                        />
                      )}
                    </div>
                  </div>
                </Card>
                <Card variant="surface" className="bg-neutral-200 dark:bg-neutral-800">
                  <div className="flex flex-row gap-2 px-4 justify-center">
                    <IconInfoCircle className="text-gray-500 dark:text-gray-400" size={20} />
                    <div>
                      {customDnsConfiguration?.customDnsIosRequiresVPN
                        ? 'The iOS VPN will set the DNS provider'
                        : 'The iOS VPN is not required for Secure DNS'}
                    </div>
                  </div>
                </Card>
              </Flex>
              {isCustom && (
                <Card className="dark:bg-neutral-800 flex flex-col gap-1" variant="surface">
                  <div className="font-semibold">Custom DNS Configured</div>
                  <div className="text-sm">
                    Your DNS configuration has been set by iVerify. Contact support to update or remove the custom
                    provider.
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">
                      iOS: The DNS provider for the DNS extension will be affected.
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">
                      {androidVpnEnabled
                        ? 'Android: The DNS provider for the VPN will be affected.'
                        : 'Android: The Private DNS provider hostname options will be affected.'}
                    </span>
                  </div>
                  <Card className="bg-neutral-100 dark:bg-neutral-700 mt-4">
                    <div className="flex gap-3 justify-between">
                      <div className="flex flex-col md:flex-row gap-1 items-center">
                        <label className="text-neutral-11 dark:text-neutral-8 font-semibold">DNS: </label>
                        <label className="text-mainText dark:text-white text-sm">Custom</label>
                      </div>
                      <div className="flex flex-col md:flex-row gap-1 items-center flex-1">
                        <label className="text-neutral-11 dark:text-neutral-8 font-semibold">Hostname: </label>
                        <label className="text-mainText dark:text-white text-sm">
                          {customDnsConfiguration?.customDnsValue
                            ?.replace(/^https:\/\//, '')
                            .replace(/\/dns-query$/, '')}
                        </label>
                      </div>
                    </div>
                  </Card>
                </Card>
              )}
              <Flex direction="column" gap="4" maxWidth="100%" className="z-40">
                {!isCustom && (
                  <Card variant="surface">
                    <div className="flex flex-col gap-2">
                      <RadioInput
                        options={options}
                        defaultOptions={defaultOptions}
                        config={dnsConfig}
                        readOnly={isSubmitting}
                      />
                    </div>
                  </Card>
                )}
                <div className="flex gap-4">
                  {!isCustom && (
                    <div className="flex flex-col gap-3 text-sm text-center items-center border border-solid border-neutral-200 dark:border-neutral-600 p-3 rounded-md w-full">
                      <p className="font-semibold">Need a different provider?</p>
                      <p className="max-w-[420px]">
                        If your organization requires a custom DNS provider, reach out to our support team and we’ll
                        configure it for you.
                      </p>

                      <Button
                        size="2"
                        onClick={(event) => {
                          event.preventDefault()
                          window.location.href = 'mailto:support@iverify.io'
                        }}
                        className="w-40 text-mainText bg-neutral-2 border border-solid border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-800"
                      >
                        Contact Support
                      </Button>
                    </div>
                  )}

                  {!customDnsConfiguration?.customDnsIosRequiresVPN && (
                    <div className="flex flex-col gap-3 text-sm text-center items-center border border-solid border-neutral-200 dark:border-neutral-600 p-3 rounded-md w-full">
                      <p className="font-semibold">Need a DNS provider set for the iOS VPN?</p>
                      <p className="max-w-[420px]">
                        If your organization requires a DNS provider to be set for the iVerify VPN on iOS, reach out to
                        our support team to learn more.
                      </p>

                      <Button
                        size="2"
                        onClick={(event) => {
                          event.preventDefault()
                          window.location.href = 'mailto:support@iverify.io'
                        }}
                        className="w-40 text-mainText bg-neutral-2 border border-solid border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-800"
                      >
                        Contact Support
                      </Button>
                    </div>
                  )}
                </div>
              </Flex>
            </div>
          </form>
        )}
      </Flex>
    </div>
  )
}

export default SecureDns
